/**
 * ConnectedPreviewCanvas — Context-connected wrapper for IframePreviewCanvas.
 *
 * Builds all callback closures from the PageBuilder context and passes them
 * as props to IframePreviewCanvas. This eliminates 26 drilled props from
 * PageBuilder.js without modifying IframePreviewCanvas's internal implementation.
 */

import React, { useCallback } from 'react';
import { IframePreviewCanvas } from './IframePreviewCanvas';
import { usePageBuilder } from '../context/PageBuilderContext';
import { getComponentAtPath } from '../utils/treeUtils';

export default function ConnectedPreviewCanvas() {
  const { doc, selection, chrome, responsive, handlers } = usePageBuilder();
  const { sections, pageConfig, updateComponentProp } = doc;
  const { selectedSection, setSelectedSection } = selection;
  const { zoomLevel, setZoomLevel, placementMode, cancelPlacement, modals } = chrome;
  const { breakpoint } = responsive;
  const { handleQuickUpdate, handleDeleteSection, addSectionDirectly,
    handleLayersShowComponentPalette, handlePlacementComplete,
    handleSectionPlacementComplete } = handlers;

  const selectedSectionId = selectedSection?.id;
  const selectedElementPath = selectedSection?.content?.selectedComponentPath || null;
  const selectedComponent = getComponentAtPath(
    selectedSection?.content?.components,
    selectedSection?.content?.selectedComponentPath
  );

  const onSectionSelect = useCallback((sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      setSelectedSection(section);
    }
  }, [sections, setSelectedSection]);

  const onElementSelect = useCallback((sectionId, elementPath) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      if (elementPath && section.section_type === 'layout-1') {
        setSelectedSection({
          ...section,
          content: { ...section.content, selectedComponentPath: elementPath }
        });
        handleQuickUpdate(sectionId, { selectedComponentPath: elementPath });
      } else {
        setSelectedSection(section);
      }
    }
  }, [sections, setSelectedSection, handleQuickUpdate]);

  const onDuplicateElement = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (sectionId && componentPath) {
      handleQuickUpdate(sectionId, { layoutAction: 'duplicateComponent', componentPath });
    }
  }, [selectedSection, handleQuickUpdate]);

  const onDeleteElement = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (sectionId && componentPath) {
      handleQuickUpdate(sectionId, { layoutAction: 'removeComponent', componentPath });
    }
  }, [selectedSection, handleQuickUpdate]);

  const onDeleteWithConfirmation = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (sectionId && componentPath) {
      if (window.confirm('Delete this component?')) {
        handleQuickUpdate(sectionId, { layoutAction: 'removeComponent', componentPath });
      }
    } else if (sectionId && !componentPath) {
      handleDeleteSection(sectionId);
    }
  }, [selectedSection, handleQuickUpdate, handleDeleteSection]);

  const onForceDelete = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (sectionId && componentPath) {
      handleQuickUpdate(sectionId, { layoutAction: 'removeComponent', componentPath });
    } else if (sectionId && !componentPath) {
      handleDeleteSection(sectionId, { skipConfirmation: true });
    }
  }, [selectedSection, handleQuickUpdate, handleDeleteSection]);

  const onAddComponent = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (sectionId && componentPath) {
      handleLayersShowComponentPalette(sectionId, componentPath, 0);
    }
  }, [selectedSection, handleLayersShowComponentPalette]);

  const onAddSiblingAfter = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (!sectionId || !componentPath) return;
    const parts = componentPath.split('.');
    let parentPath;
    let insertIndex;
    if (parts.length === 1) {
      parentPath = 'root';
      insertIndex = parseInt(parts[0]) + 1;
    } else {
      parentPath = parts.slice(0, -2).join('.');
      insertIndex = parseInt(parts[parts.length - 1]) + 1;
    }
    handleLayersShowComponentPalette(sectionId, parentPath, insertIndex);
  }, [selectedSection, handleLayersShowComponentPalette]);

  const onMoveUp = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (sectionId && componentPath) {
      handleQuickUpdate(sectionId, { layoutAction: 'moveUp', componentPath });
    }
  }, [selectedSection, handleQuickUpdate]);

  const onMoveDown = useCallback(() => {
    const sectionId = selectedSection?.id;
    const componentPath = selectedSection?.content?.selectedComponentPath;
    if (sectionId && componentPath) {
      handleQuickUpdate(sectionId, { layoutAction: 'moveDown', componentPath });
    }
  }, [selectedSection, handleQuickUpdate]);

  const onZoom = useCallback((deltaY) => {
    const zoomSensitivity = 0.01;
    const zoomFactor = 1 - (deltaY * zoomSensitivity);
    const clampedFactor = Math.max(0.9, Math.min(1.1, zoomFactor));
    setZoomLevel(prev => {
      const newZoom = prev * clampedFactor;
      return Math.min(200, Math.max(25, Math.round(newZoom)));
    });
  }, [setZoomLevel]);

  const onInlineEditEnd = useCallback((sectionId, elementPath, newContent) => {
    if (!sectionId || !elementPath || newContent === undefined) return;
    const section = sections.find(s => s.id === sectionId);
    if (!section || section.section_type !== 'layout-1') return;

    // Resolve the component to determine which property to update
    const component = getComponentAtPath(section.content?.components, elementPath);
    if (!component) return;

    let propUpdate = {};
    if (component.type === 'text') {
      propUpdate = { content: newContent };
    } else if (component.type === 'button' || component.type === 'link') {
      propUpdate = { text: newContent };
    } else {
      propUpdate = { content: newContent };
    }

    // Ensure the component is selected before updating
    if (section.content?.selectedComponentPath !== elementPath) {
      handleQuickUpdate(sectionId, { selectedComponentPath: elementPath });
    }

    // Direct component prop update (hot path)
    updateComponentProp(sectionId, elementPath, propUpdate);
  }, [sections, handleQuickUpdate, updateComponentProp]);

  const onChangeMedia = useCallback(() => {
    modals.mediaLibrary.open();
  }, [modals]);

  const onAddBlankSection = useCallback(() => {
    addSectionDirectly('layout-1', { openPicker: true });
  }, [addSectionDirectly]);

  const onOpenTemplateLibrary = useCallback(() => {
    modals.templateLibrary.open();
  }, [modals]);

  return (
    <IframePreviewCanvas
      sections={sections}
      pageConfig={pageConfig}
      breakpoint={breakpoint}
      zoomLevel={zoomLevel}
      selectedSectionId={selectedSectionId}
      selectedElementPath={selectedElementPath}
      selectedComponent={selectedComponent}
      onSectionSelect={onSectionSelect}
      onElementSelect={onElementSelect}
      onDuplicateElement={onDuplicateElement}
      onDeleteElement={onDeleteElement}
      onDeleteWithConfirmation={onDeleteWithConfirmation}
      onForceDelete={onForceDelete}
      onAddComponent={onAddComponent}
      onAddSiblingAfter={onAddSiblingAfter}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onZoom={onZoom}
      onInlineEditEnd={onInlineEditEnd}
      onChangeMedia={onChangeMedia}
      onAddBlankSection={onAddBlankSection}
      onOpenTemplateLibrary={onOpenTemplateLibrary}
      placementMode={placementMode}
      onPlacementComplete={handlePlacementComplete}
      onPlacementCancel={cancelPlacement}
      onSectionPlacementComplete={handleSectionPlacementComplete}
    />
  );
}
